import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Params } from '@angular/router';
import { AssetDto, GalleryDto, Section } from '@picflow/types';
import { Observable, catchError, forkJoin, from, map, of, switchMap, take, throwError } from 'rxjs';
import { billingScopes } from '../../billing-consts';
import { defaultColorLabels } from '../../configs/default-color-labels';
import { filterAssets } from '../../gallery-shared/lib/filter-assets';
import { parseAssetsFilters } from '../../gallery-shared/lib/parse-assets-filter';
import { SortAssets } from '../../gallery-shared/lib/sort-assets-sorting-and-grouping';
import { ActivityService } from '../../services/activity.service';
import { AssetService } from '../../services/asset.service';
import { PermissionService } from '../../services/permission.service';
import { SectionService } from '../../services/section.service';
import { StateService } from '../../services/state.service';
import { StorageService } from '../../services/storage.service';
import { DisplayGuestEmailDto, DisplayUserEmailDto } from '../../services/visitor.service';

@Injectable({
  providedIn: 'root',
})
export class AssetPageDataService {
  private assetService = inject(AssetService);
  private sectionService = inject(SectionService);
  private storage = inject(StorageService);
  private state = inject(StateService);
  private activityService = inject(ActivityService);
  private permissionService = inject(PermissionService);
  private platformId = inject<string>(PLATFORM_ID);

  private getAssets(galleryId: string): Observable<AssetDto[]> {
    if (isPlatformServer(this.platformId)) return of([]);
    return from(this.assetService.getAssetsForGallery(galleryId)).pipe(take(1));
  }

  private getSections(galleryId: string): Observable<Section[]> {
    if (isPlatformServer(this.platformId)) return of([]);
    return this.sectionService.getSections(galleryId).pipe(take(1));
  }

  private getAsset(assetId: string, galleryId: string) {
    return from(this.assetService.getAsset(assetId, galleryId)).pipe(
      take(1),
      catchError(err => {
        if (!(err instanceof HttpErrorResponse)) return throwError(err);
        return of(null);
      })
    );
  }

  public get(gallery: GalleryDto, assetId: string, queryParams: Params, isAdvancedMode = false) {
    const preferences = isAdvancedMode ? this.storage.get('advanced-mode-preferences') : undefined;
    const sorting = preferences?.sortingAndGrouping || 'auto';
    const filters = parseAssetsFilters(queryParams.filters) || [];
    return forkJoin([this.getAssets(gallery.id), this.getSections(gallery.id)]).pipe(
      switchMap(([assets, sections]) => {
        return this.activityService
          .getGalleryAssetUserFeedbackMap(gallery, assets)
          .pipe(map(feedbackMap => ({ assets, sections, feedbackMap })));
      }),
      map(({ assets, sections, feedbackMap }) => {
        let colorLabels = gallery.color_labels;
        if (!this.permissionService.canDisplayElement(billingScopes.asset.customLabels))
          colorLabels = defaultColorLabels;

        return {
          filters,
          assets,
          sections,
          colorLabels,
          feedbackMap,
        };
      }),
      switchMap(({ filters, assets, sections, colorLabels, feedbackMap }) => {
        const filteredAssets = SortAssets(
          filterAssets(assets, feedbackMap, filters, queryParams.search, false, this.state.unseenActivityCountMap),
          sorting,
          sections,
          colorLabels
        );

        return this.getAsset(assetId, gallery.id).pipe(
          map(asset => {
            if (isPlatformServer(this.platformId)) return { asset, assets: filteredAssets, sections };
            if (!asset) return;
            return { asset, assets: filteredAssets, sections };
          })
        );
      })
    );
  }

  public getGalleryPeople(
    gallery: GalleryDto,
    people: {
      userOrGuest: DisplayUserEmailDto | DisplayGuestEmailDto;
      assetIds: string[];
      type: 'user' | 'guest';
    }[] = []
  ) {
    return this.activityService.getCurrentGalleryActivity(gallery).pipe(
      take(1),
      map(activities => {
        people.length = 0;
        activities.forEach(activity => {
          if (activity.user) {
            if (!people.find(p => p.userOrGuest.id === activity.user['id'])) {
              people.push({ userOrGuest: <DisplayUserEmailDto>activity.user, assetIds: [], type: 'user' });
            }
          }
          if (activity.guest) {
            if (!people.find(p => p.userOrGuest.id === activity.guest['id'])) {
              people.push({ userOrGuest: <DisplayGuestEmailDto>activity.guest, assetIds: [], type: 'guest' });
            }
          }

          const person = people.find(p => {
            if (activity.user) return p.userOrGuest.id === activity.user?.['id'];
            if (activity.guest) return p.userOrGuest.id === activity.guest?.['id'];
          });

          if (person) {
            person.assetIds.push(activity.asset);
          }
        });

        return people;
      })
    );
  }
}
